/**
 * CrewList Component
 *
 * Fetches and displays a list of crew members with filtering, sorting,
 * pagination and bulk selection.
 *
 * The state filter defaults to the active roster. Archived crew are still full
 * members — they can sign in and respond, and responding restores them — but
 * the everyday view is who you are actually scheduling, so they are one filter
 * away rather than mixed in.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
  listUsers,
  bulkSetArchived,
  bulkSetRates,
  type BulkRatesInput,
  type User,
  type UserRole,
  type ListUsersParams,
  type MemberStateFilter,
  type CrewSortBy,
  type SortDir,
} from '../../api/users';
import { getErrorMessage } from '../../api/client';
import CrewTable from './CrewTable';
import CrewBulkBar from './CrewBulkBar';
import CrewBulkRatesModal from './CrewBulkRatesModal';
import { ComposeMessageModal } from '../messages';
import { SearchInput, Pagination, EmptyState, ErrorMessage, ConfirmDialog, Select, type SelectOption } from '../ui';
import { useAuth } from '../../context/AuthContext';
import { useOrganization } from '../../context/OrganizationContext';

interface CrewListProps {
  initialFilters?: ListUsersParams;
  showFilters?: boolean;
  limit?: number;
  /** Bump to trigger an external refetch (e.g., after CSV import) without losing filter/scroll state. */
  refetchToken?: number;
  /** Selection + bulk actions. Off for embedded read-only lists. */
  enableBulkActions?: boolean;
}

interface CrewListState {
  users: User[];
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

const roleOptions: SelectOption<UserRole | ''>[] = [
  { value: '', label: 'All Roles' },
  { value: 'CREW', label: 'Crew' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'OWNER', label: 'Owner' },
];

const stateOptions: SelectOption<MemberStateFilter>[] = [
  { value: 'ACTIVE', label: 'Active roster' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'DEACTIVATED', label: 'Deactivated' },
  { value: 'ALL', label: 'Everyone' },
];

/** Quote a CSV cell the same way the crew import template does. */
function csvCell(value: string | null | undefined): string {
  const text = value ?? '';
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export default function CrewList({
  initialFilters = {},
  showFilters = true,
  limit = 20,
  refetchToken = 0,
  enableBulkActions = false,
}: CrewListProps) {
  const [state, setState] = useState<CrewListState>({
    users: [],
    loading: true,
    error: null,
    pagination: {
      page: 1,
      limit,
      total: 0,
      totalPages: 0,
    },
  });

  const [filters, setFilters] = useState<ListUsersParams>({
    state: 'ACTIVE',
    sortBy: 'name',
    sortDir: 'asc',
    ...initialFilters,
    page: 1,
    limit,
  });

  const [searchInput, setSearchInput] = useState(initialFilters.search || '');
  const { user: currentUser } = useAuth();
  const { paymentTrackingEnabled } = useOrganization();

  // Selection is by id and survives paging, so an admin can gather a
  // graduating class across pages before acting once.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [pendingBulk, setPendingBulk] = useState<'archive' | 'restore' | null>(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showRatesModal, setShowRatesModal] = useState(false);
  const [ratesError, setRatesError] = useState<string | null>(null);

  // Row snapshots for every id ever seen this session. Rows are only
  // selectable while visible, so every selected id has a snapshot here even
  // after paging away — which is what lets the message modal and the CSV act
  // on the WHOLE selection, not just the rows currently on screen.
  const [seenRows, setSeenRows] = useState<Map<string, User>>(new Map());
  useEffect(() => {
    if (state.users.length === 0) return;
    setSeenRows((prev) => {
      const next = new Map(prev);
      for (const u of state.users) next.set(u.id, u);
      return next;
    });
  }, [state.users]);

  const selectedUsers = useMemo(
    () => [...selectedIds].map((id) => seenRows.get(id)).filter((u): u is User => u !== undefined),
    [selectedIds, seenRows]
  );

  const fetchUsers = useCallback(async (params: ListUsersParams) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await listUsers(params);
      setState({
        users: response.users,
        loading: false,
        error: null,
        pagination: response.pagination,
      });
    } catch (err) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load crew members',
      }));
    }
  }, []);

  useEffect(() => {
    fetchUsers(filters);
  }, [filters, fetchUsers, refetchToken]);

  const handleRoleChange = (role: UserRole | '') => {
    setFilters(prev => ({
      ...prev,
      role: role || undefined,
      page: 1,
    }));
  };

  const handleStateChange = (memberState: MemberStateFilter) => {
    // Clearing the selection on a filter change is the safe reading: the rows
    // you picked are about to leave the screen, and acting on a selection you
    // can no longer see is how bulk actions surprise people.
    setSelectedIds(new Set());
    setFilters(prev => ({ ...prev, state: memberState, page: 1 }));
  };

  const handleSort = (column: CrewSortBy) => {
    setFilters(prev => {
      // Re-clicking the active column flips direction. A new column starts
      // ascending, except last login: "who has not signed in" is the question
      // that column exists to answer, so it opens oldest-first.
      const sameColumn = prev.sortBy === column;
      const nextDir: SortDir = sameColumn
        ? prev.sortDir === 'asc'
          ? 'desc'
          : 'asc'
        : 'asc';
      return { ...prev, sortBy: column, sortDir: nextDir, page: 1 };
    });
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = state.users.filter(u => u.id !== currentUser?.id);
    const allSelected = selectable.length > 0 && selectable.every(u => selectedIds.has(u.id));
    setSelectedIds(prev => {
      const next = new Set(prev);
      for (const user of selectable) {
        if (allSelected) next.delete(user.id);
        else next.add(user.id);
      }
      return next;
    });
  };

  const runBulkArchive = async (archived: boolean) => {
    setBulkBusy(true);
    try {
      const result = await bulkSetArchived([...selectedIds], archived);
      // The server reports what it actually changed rather than what was
      // asked: admins, owners and anyone already in that state are skipped.
      // Saying so is more useful than claiming a number that isn't true.
      if (result.changed < result.requested) {
        toast.success(
          `${result.message}. ${result.requested - result.changed} skipped (admins, owners, or already ${
            archived ? 'archived' : 'on the roster'
          }).`
        );
      } else {
        toast.success(result.message);
      }
      setSelectedIds(new Set());
      fetchUsers(filters);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBulkBusy(false);
      setPendingBulk(null);
    }
  };

  const runBulkRates = async (rates: BulkRatesInput) => {
    setBulkBusy(true);
    setRatesError(null);
    try {
      const result = await bulkSetRates([...selectedIds], rates);
      // Deactivated members and ids from elsewhere are skipped server-side.
      if (result.changed < result.requested) {
        toast.success(
          `${result.message}. ${result.requested - result.changed} skipped (not active members here).`
        );
      } else {
        toast.success(result.message);
      }
      setShowRatesModal(false);
      setSelectedIds(new Set());
      fetchUsers(filters);
    } catch (err) {
      setRatesError(getErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const handleExport = () => {
    const rows = selectedUsers.map(u =>
      [
        csvCell(u.firstName),
        csvCell(u.lastName),
        csvCell(u.email),
        csvCell(u.phone),
        csvCell(u.role),
        csvCell(u.memberState ?? 'ACTIVE'),
        csvCell(u.lastLoginAt ? new Date(u.lastLoginAt).toISOString() : ''),
        String(u.assignmentCount ?? 0),
      ].join(',')
    );
    const csv = [
      'firstName,lastName,email,phone,role,state,lastLoginAt,assignments',
      ...rows,
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'crew-export.csv';
    link.click();
    URL.revokeObjectURL(url);

    if (selectedUsers.length < selectedIds.size) {
      // Snapshots exist for every selected row, so this is belt-and-braces.
      toast(
        `Exported ${selectedUsers.length} of ${selectedIds.size} selected.`
      );
    }
  };

  const { users, loading, error, pagination } = state;
  const showingArchived = filters.state === 'ARCHIVED';

  return (
    <div>
      {/* Filters */}
      {showFilters && (
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <SearchInput
            value={searchInput}
            onChange={setSearchInput}
            onSubmit={() => setFilters(prev => ({ ...prev, search: searchInput || undefined, page: 1 }))}
            placeholder="Search by name or email..."
            className="flex-1"
          />

          {/* Role filter */}
          <Select
            value={filters.role || ''}
            options={roleOptions}
            onChange={handleRoleChange}
          />

          {/* Membership state filter (per-org, not the platform account state) */}
          <Select
            value={filters.state ?? 'ACTIVE'}
            options={stateOptions}
            onChange={handleStateChange}
          />
        </div>
      )}

      {/* Bulk action bar */}
      {enableBulkActions && (
        <CrewBulkBar
          selectedCount={selectedIds.size}
          showingArchived={showingArchived}
          busy={bulkBusy}
          onArchive={() => setPendingBulk('archive')}
          onRestore={() => setPendingBulk('restore')}
          onMessage={() => setShowMessageModal(true)}
          onExport={handleExport}
          onUpdateRates={paymentTrackingEnabled ? () => setShowRatesModal(true) : undefined}
          onClear={() => setSelectedIds(new Set())}
        />
      )}

      {/* Error message */}
      {error && (
        <ErrorMessage
          message={error}
          onRetry={() => fetchUsers(filters)}
          className="mb-6"
        />
      )}

      {/* Users table */}
      {(loading || users.length > 0) && (
        <CrewTable
          users={users}
          loading={loading}
          selectedIds={enableBulkActions ? selectedIds : undefined}
          onToggleSelect={enableBulkActions ? toggleSelect : undefined}
          onToggleSelectAll={enableBulkActions ? toggleSelectAll : undefined}
          sortBy={filters.sortBy}
          sortDir={filters.sortDir}
          onSort={handleSort}
          currentUserId={currentUser?.id}
        />
      )}

      {/* Empty state */}
      {!loading && users.length === 0 && (
        <div className="bg-white border border-hairline">
          <EmptyState
            title={showingArchived ? 'No archived crew' : 'No crew members found'}
            message={
              showingArchived
                ? 'Archived crew stay off your roster until they respond to an event.'
                : filters.search || filters.role
                ? 'Try adjusting your filters'
                : 'Get started by inviting your first crew member'
            }
            icon={
              <svg className="h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            }
          />
        </div>
      )}

      {/* Pagination */}
      {!loading && (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          limit={pagination.limit}
          onPageChange={handlePageChange}
          className="mt-6"
        />
      )}

      {/* Bulk archive/restore confirmation */}
      <ConfirmDialog
        isOpen={pendingBulk !== null}
        onClose={() => setPendingBulk(null)}
        onConfirm={() => runBulkArchive(pendingBulk === 'archive')}
        title={pendingBulk === 'restore' ? 'Restore to roster' : 'Archive crew'}
        message={
          pendingBulk === 'restore'
            ? `Put ${selectedIds.size} crew ${
                selectedIds.size === 1 ? 'member' : 'members'
              } back on the active roster? They will appear in the assignment picker and start receiving event notices again.`
            : `Archive ${selectedIds.size} crew ${
                selectedIds.size === 1 ? 'member' : 'members'
              }? They keep their accounts and all their history, and can still sign in and respond to events. They come off your roster, out of event notices and out of the assignment picker until they respond.`
        }
        confirmLabel={pendingBulk === 'restore' ? 'Restore' : 'Archive'}
        isLoading={bulkBusy}
        // Not `danger`: nothing is destroyed and one click undoes it. Painting
        // it red would make archiving feel like the same act as deactivating,
        // which is the confusion this whole split exists to end.
        variant="default"
      />

      {/* Bulk pay rates. Only offered when the org tracks payment. */}
      <CrewBulkRatesModal
        isOpen={showRatesModal}
        selectedCount={selectedIds.size}
        isSubmitting={bulkBusy}
        error={ratesError}
        onClose={() => {
          setShowRatesModal(false);
          setRatesError(null);
        }}
        onSubmit={runBulkRates}
      />

      {/* Message the selection. Named recipients reach archived crew too —
          only broadcasts skip them. */}
      <ComposeMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        onSuccess={() => setShowMessageModal(false)}
        preselectedUsers={selectedUsers.map(u => ({
          id: u.id,
          name: `${u.firstName} ${u.lastName}`,
        }))}
      />
    </div>
  );
}
